import React, { useEffect, useState, useRef, useCallback, useMemo, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { formatDate } from '../utils/helpers';
import { createPost } from '../utils/postUtils';
import ShareButton from '../components/ShareButton';
import { 
  ArrowRight, 
  TrendingUp, 
  ThumbsUp, 
  Swords, 
  Globe, 
  Lock, 
  Users, 
  Flame,
  FileText,
  PenSquare,
  Heart,
  MessageCircle,
  MapPin,
  Trophy,
  Star,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Eye,
  MessageSquare,
  Clock,
  AlertTriangle,
  Sparkles,
  Compass,
  LogIn,
  X,
  UserPlus,
  CheckCircle,
  Share2,
  ExternalLink,
  Image as ImageIcon,
  Download,
  Smartphone,
  Info,
  ChevronDown
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Badge icons mapping
const BADGE_ICONS = {
  indian_flag: "/badges/indian_flag.png",
  bjp: "/badges/bjp.png",
  inc: "/badges/inc.png",
  aap: "/badges/aap.png",
  polivox_citizen: "/badges/polivox_citizen.png",
  civic_activist: "/badges/civic_activist.png"
};

// Debounce utility
const debounce = (fn, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
};

// Skeleton loader
const FeedItemSkeleton = memo(() => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden animate-pulse">
    <div className="p-4 flex items-start gap-3">
      <div className="w-10 h-10 rounded-full bg-gray-200" />
      <div className="flex-1">
        <div className="h-4 bg-gray-200 rounded w-1/3 mb-2" />
        <div className="h-3 bg-gray-200 rounded w-1/4" />
      </div>
    </div>
    <div className="px-4 pb-2">
      <div className="h-4 bg-gray-200 rounded w-full mb-2" />
      <div className="h-4 bg-gray-200 rounded w-2/3" />
    </div>
    <div className="w-full h-48 bg-gray-200" />
  </div>
));

// Helper for relative time
const getRelativeTime = (timestamp) => {
  if (!timestamp) return '';
  const now = new Date();
  const date = new Date(timestamp);
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDate(timestamp);
};

const getImageUrl = (url) => {
  if (!url) return null;
  return url.startsWith('http') ? url : `${API_URL}${url}`;
};

// Inline Comments Component for Feed
const InlineComments = memo(({ itemId, itemType, user, onRequireLogin, initialCount = 0 }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [commentsCount, setCommentsCount] = useState(initialCount);

  const fetchComments = async () => {
    if (!isExpanded) return;
    setLoading(true);
    try {
      // Fix 1 & 2: Use correct endpoints
      // Posts: GET /api/social-posts/{id}/comments
      // Issues: GET /api/comments/{target_id} (unified system, same as detail page)
      const endpoint = itemType === 'post' 
        ? `${API_URL}/api/social-posts/${itemId}/comments`
        : `${API_URL}/api/comments/${itemId}`;
      const res = await axios.get(endpoint, { withCredentials: true });
      // Handle different response formats
      const commentsData = res.data?.comments || res.data || [];
      setComments(Array.isArray(commentsData) ? commentsData : []);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
      setComments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isExpanded) {
      fetchComments();
    }
  }, [isExpanded, itemId]);

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      onRequireLogin('comment');
      return;
    }
    if (!newComment.trim() || submitting) return;

    setSubmitting(true);
    try {
      if (itemType === 'post') {
        // Fix 1: Posts use /api/social-posts/{id}/comments with JSON { text: ... }
        await axios.post(
          `${API_URL}/api/social-posts/${itemId}/comments`,
          { text: newComment.trim() },
          { withCredentials: true }
        );
      } else {
        // Fix 2: Issues use unified /api/comments with FormData { target_id, text }
        // This matches the detail page implementation exactly
        const formData = new FormData();
        formData.append('target_id', itemId);
        formData.append('text', newComment.trim());
        await axios.post(`${API_URL}/api/comments`, formData, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      setNewComment('');
      setCommentsCount(prev => prev + 1);
      fetchComments();
      toast.success('Comment added');
    } catch (error) {
      // Fix 2: Safely handle error responses - never render objects/arrays directly
      const errorDetail = error.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'string' 
        ? errorDetail 
        : (Array.isArray(errorDetail) ? errorDetail[0]?.msg || 'Failed to add comment' : 'Failed to add comment');
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const toggleExpanded = (e) => {
    e.stopPropagation();
    if (!user) {
      onRequireLogin('comment');
      return;
    }
    setIsExpanded(!isExpanded);
  };

  return (
    <div className="border-t" onClick={(e) => e.stopPropagation()}>
      <button
        onClick={toggleExpanded}
        className="flex items-center gap-1.5 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 w-full text-left"
      >
        <MessageCircle className="h-4 w-4" />
        {isExpanded ? 'Hide Comments' : `Comment`}
        {commentsCount > 0 && <span className="text-xs text-gray-400">({commentsCount})</span>}
      </button>
      
      {isExpanded && (
        <div className="px-4 pb-3 space-y-3">
          {/* Comment Input */}
          <form onSubmit={handleSubmitComment} className="flex gap-2">
            <input
              type="text"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Write a comment..."
              className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={submitting}
              onClick={(e) => e.stopPropagation()}
            />
            <button
              type="submit"
              disabled={!newComment.trim() || submitting}
              className="px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={(e) => e.stopPropagation()}
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Post'}
            </button>
          </form>

          {/* Comments List */}
          {loading ? (
            <div className="flex justify-center py-2">
              <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
            </div>
          ) : comments.length > 0 ? (
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {comments.slice(0, 5).map((comment, idx) => (
                <div key={comment.comment_id || idx} className="flex gap-2 text-sm">
                  <img
                    src={comment.user_picture || '/logo.png'}
                    alt=""
                    className="w-7 h-7 rounded-full object-cover flex-shrink-0"
                    onError={(e) => { e.target.src = '/logo.png'; }}
                  />
                  <div className="flex-1 bg-gray-50 rounded-lg px-3 py-2">
                    <span className="font-medium text-gray-900 text-xs">{comment.user_name || 'User'}</span>
                    <p className="text-gray-700 text-xs mt-0.5">{comment.text || comment.content}</p>
                  </div>
                </div>
              ))}
              {comments.length > 5 && (
                <p className="text-xs text-gray-500 text-center">+{comments.length - 5} more comments</p>
              )}
            </div>
          ) : (
            <p className="text-xs text-gray-400 text-center py-2">No comments yet. Be the first!</p>
          )}
        </div>
      )}
    </div>
  );
});

// Memoized Post Card
const PostCard = memo(({ item, isLiked, onLike, onNavigate, onImageExpand, user, onRequireLogin }) => {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden" data-testid={`feed-post-${item.post_id}`}>
      <div className="p-4">
        <div className="flex items-center gap-3 mb-3 cursor-pointer" onClick={() => onNavigate(`/profile/${item.user_id}`)}>
          <img
            src={getImageUrl(item.user_picture) || '/logo.png'}
            alt={item.user_name}
            className="w-10 h-10 rounded-full object-cover"
            onError={(e) => { e.target.src = '/logo.png'; }}
          />
          <div>
            <span className={`text-sm font-medium ${item.is_verified ? 'text-blue-800 font-bold' : ''}`}>{item.user_name}</span>
            <p className="text-xs text-gray-500">{getRelativeTime(item.created_at)}</p>
          </div>
        </div>
        <p className="text-gray-800 mb-3 whitespace-pre-wrap text-sm cursor-pointer" onClick={() => onNavigate(`/posts/${item.post_id}`)}>
          {item.caption}
        </p>
      </div>
      
      {(item.media_url || item.image_url || item.image) && (
        <img 
          src={getImageUrl(item.media_url || item.image_url || item.image)}
          alt="Post"
          className="w-full max-h-[350px] object-contain bg-gray-100 cursor-pointer"
          onClick={() => onImageExpand(getImageUrl(item.media_url || item.image_url || item.image))}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      )}
      
      <div className="px-4 py-2 border-t flex items-center gap-4 text-xs text-gray-500">
        <span>{item.likes_count || 0} likes</span>
        <span>{item.comments_count || 0} comments</span>
      </div>
      
      <div className="px-4 py-2 border-t flex items-center gap-2">
        <button
          onClick={(e) => { e.stopPropagation(); onLike(item.post_id); }}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm ${isLiked ? 'text-red-500 bg-red-50' : 'text-gray-600 hover:bg-gray-100'}`}
        >
          <Heart className={`h-4 w-4 ${isLiked ? 'fill-current' : ''}`} />
          Like
        </button>
        <ShareButton
          title={item.caption?.substring(0, 100) || 'Post'}
          url={`${window.location.origin}/posts/${item.post_id}`}
          shareType="post"
          itemId={item.post_id}
          variant="small"
        />
      </div>
      
      {/* Inline Comments */}
      <InlineComments 
        itemId={item.post_id} 
        itemType="post" 
        user={user} 
        onRequireLogin={onRequireLogin}
        initialCount={item.comments_count || 0}
      />
    </div>
  );
});

// Issue Card Component with Inline Comments and Voting
const IssueCard = memo(({ item, onNavigate, user, onRequireLogin }) => {
  const [voteCount, setVoteCount] = useState(item.vote_count || 0);
  const [hasVoted, setHasVoted] = useState(false);
  const [voting, setVoting] = useState(false);

  // Check initial vote status
  useEffect(() => {
    if (user && item.issue_id) {
      checkVoteStatus();
    }
  }, [user, item.issue_id]);

  const checkVoteStatus = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/issues/${item.issue_id}/vote-status`, { withCredentials: true });
      setHasVoted(res.data.has_voted);
    } catch (error) {
      // Silently fail - user might not be logged in
    }
  };

  const handleVote = async (e) => {
    e.stopPropagation();
    if (!user) {
      onRequireLogin('vote');
      return;
    }
    if (voting) return;

    setVoting(true);
    try {
      const res = await axios.post(`${API_URL}/api/issues/${item.issue_id}/vote`, {}, { withCredentials: true });
      const isVoted = res.data.action === 'voted';
      setHasVoted(isVoted);
      setVoteCount(prev => isVoted ? prev + 1 : Math.max(0, prev - 1));
      toast.success(isVoted ? 'Vote counted!' : 'Vote removed');
    } catch (error) {
      const errorMessage = typeof error.response?.data?.detail === 'string'
        ? error.response.data.detail
        : 'Failed to vote';
      toast.error(errorMessage);
    } finally {
      setVoting(false);
    }
  };
  
  return (
    <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-md" data-testid={`feed-issue-${item.issue_id}`}>
      <div onClick={() => onNavigate(`/issues/${item.issue_id}`)} className="flex flex-col sm:flex-row cursor-pointer">
        <div className="sm:w-32 h-24 sm:h-auto overflow-hidden flex-shrink-0 relative bg-gradient-to-br from-pink-50 to-purple-50">
          {item.image_url ? (
            <img src={getImageUrl(item.image_url)} alt={item.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center"><FileText className="h-8 w-8 text-pink-300" /></div>
          )}
          {item.leader_photo && (
            <img src={item.leader_photo} alt="" className="absolute bottom-2 right-2 w-8 h-8 rounded-full border-2 border-white shadow-lg object-cover" onClick={(e) => { e.stopPropagation(); onNavigate(`/leaders/${item.primary_leader_id}`); }} />
          )}
        </div>
        <div className="flex-1 p-3">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-medium text-pink-600 bg-pink-50 px-2 py-0.5 rounded-full">Issue</span>
            <span className={`text-xs px-2 py-0.5 rounded-full ${item.status === 'resolved' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{item.status?.replace('_', ' ') || 'Open'}</span>
          </div>
          <h3 className="text-sm font-semibold text-gray-900 line-clamp-2 mb-1">{item.title}</h3>
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{item.constituency || item.state}</span>
          </div>
        </div>
      </div>
      
      {/* Vote and Comment Actions */}
      <div className="px-3 py-2 border-t flex items-center gap-2">
        <button
          onClick={handleVote}
          disabled={voting}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors ${
            hasVoted 
              ? 'text-blue-600 bg-blue-50 font-medium' 
              : 'text-gray-600 hover:bg-gray-100'
          }`}
          data-testid={`vote-issue-${item.issue_id}`}
        >
          <ThumbsUp className={`h-4 w-4 ${hasVoted ? 'fill-current' : ''}`} />
          <span>{voteCount}</span>
          {voting && <Loader2 className="h-3 w-3 animate-spin ml-1" />}
        </button>
      </div>
      
      {/* Inline Comments for Issues */}
      <InlineComments 
        itemId={item.issue_id} 
        itemType="issue" 
        user={user} 
        onRequireLogin={onRequireLogin}
        initialCount={item.comments_count || item.comment_count || 0}
      />
    </div>
  );
});

// Ad Card
const AdCard = memo(({ ad, isLiked, onLike, onShare, onNavigate }) => {
  const hasLink = ad.link && ad.link.trim() !== '';
  
  return (
    <div 
      onClick={() => hasLink ? window.open(ad.link, '_blank') : onNavigate(`/ads/${ad.ad_id}`)}
      className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl overflow-hidden shadow-sm border border-purple-100 cursor-pointer hover:shadow-md"
    >
      {ad.image_url && (
        <img src={getImageUrl(ad.image_url)} alt={ad.title} className="w-full max-h-[300px] object-contain" onError={(e) => e.target.style.display = 'none'} />
      )}
      <div className="p-4">
        <span className="text-xs font-medium text-purple-600 bg-purple-100 px-2 py-0.5 rounded-full">Sponsored</span>
        <h3 className="text-base font-semibold text-gray-900 mt-2">{ad.title}</h3>
        {ad.description && <p className="text-sm text-gray-600 mt-1 line-clamp-2">{ad.description}</p>}
      </div>
      <div className="flex items-center gap-3 px-4 pb-3 border-t border-purple-100">
        <button onClick={(e) => { e.stopPropagation(); onLike(ad.ad_id); }} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm ${isLiked ? 'text-red-500 bg-red-50' : 'text-gray-600 hover:bg-gray-100'}`}>
          <Heart className={`h-4 w-4 ${isLiked ? 'fill-current' : ''}`} /> Like
        </button>
        <button onClick={(e) => { e.stopPropagation(); onShare(ad); }} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-gray-600 hover:bg-gray-100">
          <Share2 className="h-4 w-4" /> Share
        </button>
      </div>
    </div>
  );
});

const Home = () => {
  const navigate = useNavigate();
  const { user, canParticipate, triggerOnboarding } = useAuth();
  
  // State
  const [trendingIssues, setTrendingIssues] = useState([]);
  const [topPerformers, setTopPerformers] = useState({ top_leaders: [], worst_leaders: [] });
  const [liveNow, setLiveNow] = useState([]);
  const [fromNetwork, setFromNetwork] = useState([]);
  const [trending, setTrending] = useState([]);
  const [discover, setDiscover] = useState([]);
  const [hasNetwork, setHasNetwork] = useState(false);
  const [feedLoading, setFeedLoading] = useState(true);
  const [likedPosts, setLikedPosts] = useState({});
  const [activeAds, setActiveAds] = useState([]);
  const [likedAds, setLikedAds] = useState({});
  const [suggestedUsers, setSuggestedUsers] = useState([]);
  const [friendRequests, setFriendRequests] = useState({});
  const [followingStatus, setFollowingStatus] = useState({});
  const [moreItems, setMoreItems] = useState([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [skip, setSkip] = useState(0);
  const loadingRef = useRef(false);
  // Session history to track recently shown items (prevents repetition within 30 items)
  const recentHistoryRef = useRef(new Set());
  const shuffleSeedRef = useRef(Date.now());
  const RECENT_HISTORY_SIZE = 30;
  const [showGuestBanner, setShowGuestBanner] = useState(!user);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showCreatePostModal, setShowCreatePostModal] = useState(false);
  const [newPostContent, setNewPostContent] = useState('');
  const [newPostImage, setNewPostImage] = useState(null);
  const [newPostImagePreview, setNewPostImagePreview] = useState(null);
  const [postingInProgress, setPostingInProgress] = useState(false);
  const [expandedImage, setExpandedImage] = useState(null);
  const [showInfoPanel, setShowInfoPanel] = useState(false);
  const [spotlightOffset, setSpotlightOffset] = useState(0);
  
  // Horizontal scroll refs
  const trendingScrollRef = useRef(null);
  const friendsScrollRef = useRef(null);
  const discoverScrollRef = useRef(null);

  useEffect(() => { setShowGuestBanner(!user); }, [user]);

  // Auto-rotate Leader Spotlight every 3 seconds
  useEffect(() => {
    const rotateInterval = setInterval(() => {
      setSpotlightOffset(prev => prev + 1);
    }, 3000); // 3 seconds
    
    return () => clearInterval(rotateInterval);
  }, []);

  // PWA Install handler
  useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) {
      setIsInstalled(true);
      return;
    }
    const dismissed = localStorage.getItem('pwa_install_dismissed');
    if (dismissed && Date.now() - parseInt(dismissed) < 24 * 60 * 60 * 1000) return;
    
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (isIOS) {
      setTimeout(() => setShowInstallBanner(true), 2000);
    } else {
      const handler = (e) => { e.preventDefault(); setDeferredPrompt(e); setTimeout(() => setShowInstallBanner(true), 2000); };
      window.addEventListener('beforeinstallprompt', handler);
      window.addEventListener('appinstalled', () => { setIsInstalled(true); setShowInstallBanner(false); });
      return () => window.removeEventListener('beforeinstallprompt', handler);
    }
  }, []);

  const requireLogin = (action) => {
    if (!user) { setShowLoginPrompt(action); return true; }
    return false;
  };

  const handleInstallClick = async () => {
    if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
      setShowIOSInstructions(true);
    } else if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') { setIsInstalled(true); setShowInstallBanner(false); }
      setDeferredPrompt(null);
    }
  };

  // Define fetch functions BEFORE useEffects that call them
  const fetchData = useCallback(async () => {
    try {
      const [issuesRes, performersRes] = await Promise.all([
        axios.get(`${API_URL}/api/issues/trending`),
        axios.get(`${API_URL}/api/leaders/top-performers`)
      ]);
      setTrendingIssues(issuesRes.data);
      setTopPerformers(performersRes.data);
    } catch (error) { console.error('Error fetching data:', error); }
  }, []);

  const fetchActiveAds = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/ads/active`);
      setActiveAds(response.data.ads || []);
    } catch (error) { console.error('Error fetching ads:', error); }
  }, []);

  const fetchSuggestedUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users/suggested`, { withCredentials: true });
      setSuggestedUsers(response.data.suggestions || []);
    } catch (error) { console.error('Error fetching suggested users:', error); }
  }, []);

  const fetchEnhancedFeed = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/feed/enhanced`, { withCredentials: true });
      const data = response.data;
      setFeedLoading(false);
      setLiveNow(data.live_now || []);
      setFromNetwork(data.from_network || []);
      setTrending(data.trending || []);
      setDiscover(data.discover || []);
      setHasNetwork(data.has_network || false);
    } catch (error) { console.error('Error fetching feed:', error); setFeedLoading(false); }
  }, []);

  // Data fetching
  useEffect(() => {
    fetchData();
    fetchEnhancedFeed();
    fetchSuggestedUsers();
    fetchActiveAds();
  }, [fetchData, fetchEnhancedFeed, fetchSuggestedUsers, fetchActiveAds]);

  // Listen for Home navigation click to refresh feed
  useEffect(() => {
    const handleRefreshFeed = () => {
      // Reset feed state and refetch
      setMoreItems([]);
      setSkip(0);
      recentHistoryRef.current.clear();
      shuffleSeedRef.current = Date.now();
      setFeedLoading(true);
      
      // Refetch all data
      fetchData();
      fetchEnhancedFeed();
      fetchSuggestedUsers();
      fetchActiveAds();
    };

    window.addEventListener('refreshHomeFeed', handleRefreshFeed);
    return () => window.removeEventListener('refreshHomeFeed', handleRefreshFeed);
  }, [fetchData, fetchEnhancedFeed, fetchSuggestedUsers, fetchActiveAds]);

  // Smart shuffle that guarantees different order each time
  const smartShuffle = useCallback((array, seed) => {
    const arr = [...array];
    let currentSeed = seed;
    for (let i = arr.length - 1; i > 0; i--) {
      currentSeed = (currentSeed * 9301 + 49297) % 233280;
      const j = Math.floor((currentSeed / 233280) * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }, []);

  // Add item to recent history
  const addToRecentHistory = useCallback((itemId) => {
    if (!itemId) return;
    recentHistoryRef.current.add(itemId);
    // Trim history if too large
    if (recentHistoryRef.current.size > RECENT_HISTORY_SIZE * 2) {
      const arr = Array.from(recentHistoryRef.current);
      recentHistoryRef.current = new Set(arr.slice(-RECENT_HISTORY_SIZE));
    }
  }, []);

  const loadMoreItems = useCallback(async () => {
    if (loadingRef.current || loadingMore) return;
    loadingRef.current = true;
    setLoadingMore(true);
    
    try {
      const response = await axios.get(`${API_URL}/api/feed/more`, { params: { skip, limit: 10 }, withCredentials: true });
      const newItems = response.data.items || [];
      
      // Filter items that are in recent history (last 30 shown)
      const uniqueItems = newItems.filter(item => {
        const itemId = item.post_id || item.issue_id || item.room_id;
        return !recentHistoryRef.current.has(itemId);
      });
      
      // If no unique items, smart recycle with different order
      if (uniqueItems.length === 0) {
        // Update seed for different shuffle order
        shuffleSeedRef.current = Date.now();
        
        // Collect all available content
        const allContent = [...trendingIssues, ...trending, ...discover].filter(i => i.issue_id || i.post_id);
        
        // Smart shuffle with new seed
        const shuffled = smartShuffle(allContent, shuffleSeedRef.current + skip);
        
        // Select items not in recent history
        const selectedItems = [];
        for (const item of shuffled) {
          const itemId = item.issue_id || item.post_id;
          if (!recentHistoryRef.current.has(itemId) && selectedItems.length < 10) {
            selectedItems.push(item);
            addToRecentHistory(itemId);
          }
        }
        
        // If still not enough (all items shown recently), clear oldest and pick
        if (selectedItems.length < 5) {
          const arr = Array.from(recentHistoryRef.current);
          recentHistoryRef.current = new Set(arr.slice(-10));
          
          for (const item of shuffled.slice(0, 10 - selectedItems.length)) {
            if (!selectedItems.find(s => (s.issue_id || s.post_id) === (item.issue_id || item.post_id))) {
              selectedItems.push(item);
            }
          }
        }
        
        const recycledItems = selectedItems.map((item, idx) => ({
          ...item,
          _feedKey: `recycled-${skip}-${idx}-${item.issue_id || item.post_id}-${shuffleSeedRef.current}`,
          feed_type: item.issue_id ? 'issue' : 'post'
        }));
        
        setMoreItems(prev => [...prev, ...recycledItems]);
      } else {
        // Add new unique items to history and state
        uniqueItems.forEach(item => {
          addToRecentHistory(item.post_id || item.issue_id || item.room_id);
        });
        setMoreItems(prev => [...prev, ...uniqueItems]);
      }
      
      setSkip(prev => prev + 10);
    } catch (error) { 
      console.error('Error loading more:', error);
      // On error, still add recycled content with smart algorithm
      shuffleSeedRef.current = Date.now();
      const existingContent = [...trendingIssues, ...trending].filter(i => i.issue_id || i.post_id);
      const shuffled = smartShuffle(existingContent, shuffleSeedRef.current);
      
      const recycledItems = shuffled.slice(0, 5).map((item, idx) => ({
        ...item,
        _feedKey: `recycled-error-${skip}-${idx}-${item.issue_id || item.post_id}-${shuffleSeedRef.current}`,
        feed_type: item.issue_id ? 'issue' : 'post'
      }));
      setMoreItems(prev => [...prev, ...recycledItems]);
    }
    finally { setLoadingMore(false); loadingRef.current = false; }
  }, [skip, loadingMore, trending, discover, trendingIssues, smartShuffle, addToRecentHistory]);

  // Infinite scroll - always continues
  useEffect(() => {
    const handleScroll = debounce(() => {
      if (loadingRef.current) return;
      const scrollPct = (window.scrollY + window.innerHeight) / document.documentElement.scrollHeight;
      if (scrollPct > 0.8) loadMoreItems();
    }, 150);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [loadMoreItems]);

  const handleLikePost = useCallback(async (postId) => {
    if (!user) { setShowLoginPrompt('like this post'); return; }
    setLikedPosts(prev => ({ ...prev, [postId]: !prev[postId] }));
    try {
      const response = await axios.post(`${API_URL}/api/social-posts/${postId}/like`, {}, { withCredentials: true });
      setLikedPosts(prev => ({ ...prev, [postId]: response.data.liked }));
      const updateFeed = items => items.map(item => item.post_id === postId ? { ...item, likes_count: response.data.likes_count } : item);
      setFromNetwork(updateFeed); setTrending(updateFeed); setDiscover(updateFeed); setMoreItems(updateFeed);
    } catch (error) { setLikedPosts(prev => ({ ...prev, [postId]: !prev[postId] })); }
  }, [user]);

  const handleAddFriend = async (targetUserId) => {
    if (requireLogin('add friends')) return;
    try {
      setFriendRequests(prev => ({ ...prev, [targetUserId]: 'sending' }));
      await axios.post(`${API_URL}/api/friends/request`, { receiver_user_id: targetUserId }, { withCredentials: true });
      setFriendRequests(prev => ({ ...prev, [targetUserId]: 'sent' }));
    } catch (error) { setFriendRequests(prev => ({ ...prev, [targetUserId]: 'error' })); }
  };

  const handleFollow = async (targetUserId) => {
    if (requireLogin('follow users')) return;
    try {
      setFollowingStatus(prev => ({ ...prev, [targetUserId]: 'following' }));
      await axios.post(`${API_URL}/api/follow/${targetUserId}`, {}, { withCredentials: true });
      setFollowingStatus(prev => ({ ...prev, [targetUserId]: 'done' }));
    } catch (error) { setFollowingStatus(prev => ({ ...prev, [targetUserId]: 'error' })); }
  };

  const handleLikeAd = useCallback((adId) => {
    if (!user) { setShowLoginPrompt('like this ad'); return; }
    setLikedAds(prev => ({ ...prev, [adId]: !prev[adId] }));
  }, [user]);

  const handleShareAd = useCallback(async (ad) => {
    if (navigator.share) {
      try { await navigator.share({ title: ad.title, url: ad.link || window.location.href }); } catch (err) { /* User cancelled */ }
    } else { navigator.clipboard.writeText(ad.link || window.location.href); }
  }, []);

  const handlePostImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setNewPostImage(file);
      const reader = new FileReader();
      reader.onloadend = () => setNewPostImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleCreatePost = async () => {
    if (!newPostContent.trim() && !newPostImage) return;
    
    // Double-check profile completion (security safeguard)
    if (!canParticipate()) {
      triggerOnboarding();
      return;
    }
    
    setPostingInProgress(true);
    try {
      await createPost({ caption: newPostContent, media: newPostImage });
      setShowCreatePostModal(false);
      setNewPostContent(''); setNewPostImage(null); setNewPostImagePreview(null);
      setTimeout(() => fetchEnhancedFeed(), 300);
    } catch (error) { 
      console.error('Post error:', error);
      // Handle 403 from backend - check for profile completion message
      if (error.response?.status === 403) {
        const detail = error.response?.data?.detail || '';
        if (detail.toLowerCase().includes('complete your responsible citizen profile')) {
          setShowCreatePostModal(false);
          triggerOnboarding();
        } else {
          // Other 403 errors (e.g., banned user) - show toast
          toast.error(detail || 'You are not authorized to perform this action');
        }
      } else {
        toast.error(error.response?.data?.detail || 'Failed to create post');
      }
    }
    finally { setPostingInProgress(false); }
  };

  // Horizontal scroll helper
  const scrollHorizontal = (ref, direction) => {
    if (ref.current) {
      ref.current.scrollBy({ left: direction === 'left' ? -300 : 300, behavior: 'smooth' });
    }
  };

  // Generate TRUE infinite feed with smart non-repeating algorithm
  const generateInfiniteFeed = useMemo(() => {
    // Collect all content
    const civicContent = [...trendingIssues].map(item => ({ 
      ...item, 
      feed_type: 'issue', 
      contentType: 'civic',
      _sourceId: item.issue_id 
    }));
    const socialContent = [...trending, ...discover, ...fromNetwork]
      .filter(i => i.feed_type === 'post' || i.post_id)
      .map(item => ({ 
        ...item, 
        feed_type: 'post', 
        contentType: 'social',
        _sourceId: item.post_id 
      }));
    
    // Create multiple shuffled pools for variety
    const seed1 = shuffleSeedRef.current;
    const seed2 = shuffleSeedRef.current + 12345;
    const seed3 = shuffleSeedRef.current + 67890;
    
    const civicPool1 = smartShuffle(civicContent, seed1);
    const civicPool2 = smartShuffle(civicContent, seed2);
    const socialPool1 = smartShuffle(socialContent, seed1);
    const socialPool2 = smartShuffle(socialContent, seed3);
    
    // Build feed with smart selection
    const feed = [];
    let civicIdx = 0, socialIdx = 0, adIdx = 0;
    let useCivicPool2 = false, useSocialPool2 = false;
    const totalItems = 100;
    
    // Track items added in this generation to avoid immediate repeats
    const addedInGeneration = new Set();
    
    // Define where to insert horizontal sections (naturally spaced)
    const trendingPositions = [3, 25, 55, 85];
    const friendsPositions = [8, 35, 65];
    const discoverPositions = [15, 45, 75];
    
    for (let i = 0; i < totalItems; i++) {
      const uniqueKey = `feed-${i}-${shuffleSeedRef.current}`;
      
      // Insert Trending Issues carousel
      if (trendingPositions.includes(i) && trendingIssues.length > 0) {
        const startIdx = (trendingPositions.indexOf(i) * 3 + Math.floor(shuffleSeedRef.current % 5)) % trendingIssues.length;
        feed.push({
          feed_type: 'widget_trending',
          _feedKey: `trending-widget-${i}`,
          startIndex: startIdx
        });
      }
      
      // Insert Suggested Friends carousel
      if (friendsPositions.includes(i) && suggestedUsers.length > 0) {
        feed.push({
          feed_type: 'widget_friends',
          _feedKey: `friends-widget-${i}`
        });
      }
      
      // Insert Discover carousel
      if (discoverPositions.includes(i) && discover.length > 0) {
        const startIdx = (discoverPositions.indexOf(i) * 2 + Math.floor(shuffleSeedRef.current % 4)) % discover.length;
        feed.push({
          feed_type: 'widget_discover',
          _feedKey: `discover-widget-${i}`,
          startIndex: startIdx
        });
      }
      
      // Insert ad every 12 items
      if (i > 0 && i % 12 === 0 && activeAds.length > 0) {
        const ad = activeAds[adIdx % activeAds.length];
        feed.push({ ...ad, feed_type: 'ad', _feedKey: `ad-${i}-${ad.ad_id}` });
        adIdx++;
      }
      
      // Determine content type based on 60/40 ratio with some randomness
      // Pattern creates natural mixing: civic, civic, social, civic, civic, social...
      const basePattern = [0, 0, 1, 0, 0, 1, 0, 1, 0, 1]; // 60% civic, 40% social
      const patternIdx = i % 10;
      const isSocial = basePattern[patternIdx] === 1;
      
      // Select content with anti-repetition logic
      let selectedItem = null;
      
      if (isSocial && socialContent.length > 0) {
        // Alternate between shuffled pools for variety
        const pool = useSocialPool2 ? socialPool2 : socialPool1;
        
        // Find item not recently used
        for (let attempt = 0; attempt < pool.length; attempt++) {
          const idx = (socialIdx + attempt) % pool.length;
          const candidate = pool[idx];
          const itemId = candidate._sourceId;
          
          if (!addedInGeneration.has(itemId)) {
            selectedItem = candidate;
            addedInGeneration.add(itemId);
            socialIdx = (idx + 1) % pool.length;
            break;
          }
        }
        
        // If all items used in this generation, clear and pick
        if (!selectedItem && pool.length > 0) {
          // Switch to other pool for fresh items
          useSocialPool2 = !useSocialPool2;
          const altPool = useSocialPool2 ? socialPool2 : socialPool1;
          selectedItem = altPool[socialIdx % altPool.length];
          socialIdx = (socialIdx + 1) % altPool.length;
        }
      }
      
      if (!selectedItem && civicContent.length > 0) {
        const pool = useCivicPool2 ? civicPool2 : civicPool1;
        
        for (let attempt = 0; attempt < pool.length; attempt++) {
          const idx = (civicIdx + attempt) % pool.length;
          const candidate = pool[idx];
          const itemId = candidate._sourceId;
          
          if (!addedInGeneration.has(itemId)) {
            selectedItem = candidate;
            addedInGeneration.add(itemId);
            civicIdx = (idx + 1) % pool.length;
            break;
          }
        }
        
        if (!selectedItem && pool.length > 0) {
          useCivicPool2 = !useCivicPool2;
          const altPool = useCivicPool2 ? civicPool2 : civicPool1;
          selectedItem = altPool[civicIdx % altPool.length];
          civicIdx = (civicIdx + 1) % altPool.length;
        }
      }
      
      if (selectedItem) {
        feed.push({ 
          ...selectedItem, 
          _feedKey: `${uniqueKey}-${selectedItem.feed_type}-${selectedItem._sourceId}` 
        });
      }
      
      // Reset generation tracking periodically to allow items to reappear later
      if (i > 0 && i % 30 === 0) {
        addedInGeneration.clear();
      }
    }
    
    return feed;
  }, [trendingIssues, trending, discover, fromNetwork, activeAds, suggestedUsers, smartShuffle]);

  // Combined feed for infinite scroll - always continues
  const combinedFeed = useMemo(() => {
    const baseFeed = [...generateInfiniteFeed];
    
    // Add more items from infinite scroll
    if (moreItems.length > 0) {
      moreItems.forEach((item, idx) => {
        baseFeed.push({ ...item, _feedKey: `more-${idx}-${item.post_id || item.issue_id}-${Date.now()}` });
      });
    }
    
    return baseFeed;
  }, [generateInfiniteFeed, moreItems]);

  // Render feed item based on type - including widgets
  const renderFeedItem = useCallback((item) => {
    // Trending Issues Widget
    if (item.feed_type === 'widget_trending') {
      const startIdx = item.startIndex || 0;
      const itemsToShow = trendingIssues.slice(startIdx, startIdx + 6).concat(
        trendingIssues.slice(0, Math.max(0, 6 - (trendingIssues.length - startIdx)))
      ).slice(0, 6);
      
      return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 my-2">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-blue-600" />
              <h3 className="text-sm font-semibold text-gray-900">Trending Issues</h3>
            </div>
            <button onClick={() => navigate('/issues')} className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1">
              View All <ArrowRight className="h-3 w-3" />
            </button>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide" style={{ scrollbarWidth: 'none' }}>
            {itemsToShow.map((issue, idx) => (
              <div key={`trending-${issue.issue_id}-${idx}`} onClick={() => navigate(`/issues/${issue.issue_id}`)} className="flex-shrink-0 w-44 bg-gray-50 rounded-lg p-2.5 cursor-pointer hover:bg-gray-100 transition-colors border border-gray-100">
                <div className="flex items-start gap-2">
                  <span className="text-sm font-bold text-gray-300">#{idx + 1}</span>
                  <div className="min-w-0 flex-1">
                    <h4 className="text-xs font-medium text-gray-900 line-clamp-2 mb-1">{issue.title}</h4>
                    <div className="flex items-center gap-2 text-[10px] text-gray-500">
                      <span className="truncate">{issue.constituency || issue.state}</span>
                      <span className="flex items-center gap-0.5"><ThumbsUp className="h-2.5 w-2.5" />{issue.vote_count}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }
    
    // Suggested Friends Widget
    if (item.feed_type === 'widget_friends') {
      const usersToShow = suggestedUsers.slice(0, 6);
      if (usersToShow.length === 0) return null;
      
      return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 my-2">
          <div className="flex items-center gap-2 mb-3">
            <UserPlus className="h-4 w-4 text-purple-600" />
            <h3 className="text-sm font-semibold text-gray-900">People You May Know</h3>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-1 scrollbar-hide" style={{ scrollbarWidth: 'none' }}>
            {usersToShow.map((sugUser) => (
              <div key={`friend-${sugUser.user_id}`} className="flex-shrink-0 w-32 bg-gray-50 rounded-lg p-3 text-center border border-gray-100">
                <img src={sugUser.picture || '/logo.png'} alt={sugUser.name} className="w-10 h-10 rounded-full mx-auto mb-2 object-cover border-2 border-gray-100" onError={(e) => e.target.src = '/logo.png'} />
                <p className="text-xs font-medium text-gray-900 truncate mb-2">{sugUser.name}</p>
                <div className="flex gap-1">
                  <button 
                    onClick={() => handleAddFriend(sugUser.user_id)}
                    disabled={friendRequests[sugUser.user_id] === 'sent'}
                    className={`flex-1 text-[10px] px-1.5 py-1 rounded ${friendRequests[sugUser.user_id] === 'sent' ? 'bg-gray-100 text-gray-500' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'}`}
                  >
                    {friendRequests[sugUser.user_id] === 'sent' ? 'Sent' : 'Add'}
                  </button>
                  <button 
                    onClick={() => handleFollow(sugUser.user_id)}
                    disabled={followingStatus[sugUser.user_id] === 'done'}
                    className={`flex-1 text-[10px] px-1.5 py-1 rounded ${followingStatus[sugUser.user_id] === 'done' ? 'bg-green-100 text-green-700' : 'bg-purple-100 text-purple-700 hover:bg-purple-200'}`}
                  >
                    {followingStatus[sugUser.user_id] === 'done' ? '✓' : 'Follow'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }
    
    // Discover Widget
    if (item.feed_type === 'widget_discover') {
      const startIdx = item.startIndex || 0;
      const itemsToShow = discover.slice(startIdx, startIdx + 5).concat(
        discover.slice(0, Math.max(0, 5 - (discover.length - startIdx)))
      ).slice(0, 5);
      
      if (itemsToShow.length === 0) return null;
      
      return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 my-2">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Compass className="h-4 w-4 text-purple-600" />
              <h3 className="text-sm font-semibold text-gray-900">Discover</h3>
            </div>
            <button onClick={() => navigate('/discover')} className="text-xs text-purple-600 hover:text-purple-700 flex items-center gap-1">
              More <ArrowRight className="h-3 w-3" />
            </button>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-1 scrollbar-hide" style={{ scrollbarWidth: 'none' }}>
            {itemsToShow.map((discoverItem, idx) => (
              <div key={`discover-${discoverItem.issue_id || discoverItem.post_id}-${idx}`} onClick={() => navigate(discoverItem.issue_id ? `/issues/${discoverItem.issue_id}` : `/posts/${discoverItem.post_id}`)} className="flex-shrink-0 w-48 bg-white rounded-lg overflow-hidden shadow-sm border border-gray-100 cursor-pointer hover:shadow-md transition-shadow">
                <div className="h-20 bg-gradient-to-br from-purple-50 to-blue-50 overflow-hidden">
                  {(discoverItem.image_url || discoverItem.media_url) ? (
                    <img src={getImageUrl(discoverItem.image_url || discoverItem.media_url)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center"><FileText className="h-6 w-6 text-purple-300" /></div>
                  )}
                </div>
                <div className="p-2">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${discoverItem.issue_id ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                    {discoverItem.issue_id ? 'Issue' : 'Post'}
                  </span>
                  <h4 className="text-xs font-medium text-gray-900 line-clamp-1 mt-1">{discoverItem.title || discoverItem.caption?.substring(0, 40)}</h4>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }
    
    // Ad Card
    if (item.feed_type === 'ad') {
      return <AdCard ad={item} isLiked={likedAds[item.ad_id]} onLike={handleLikeAd} onShare={handleShareAd} onNavigate={navigate} />;
    }
    
    // Issue Card
    if (item.feed_type === 'issue' || item.issue_id) {
      return <IssueCard item={item} onNavigate={navigate} user={user} onRequireLogin={requireLogin} />;
    }
    
    // Post Card
    if (item.feed_type === 'post' || item.post_id) {
      return <PostCard item={item} isLiked={likedPosts[item.post_id]} onLike={handleLikePost} onNavigate={navigate} onImageExpand={setExpandedImage} user={user} onRequireLogin={requireLogin} />;
    }
    
    return null;
  }, [trendingIssues, suggestedUsers, discover, likedAds, handleLikeAd, handleShareAd, navigate, likedPosts, handleLikePost, user, friendRequests, followingStatus, handleAddFriend, handleFollow]);

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Login Prompt Modal */}
      {showLoginPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl p-6 max-w-sm mx-4 shadow-xl">
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-2">
                <LogIn className="h-5 w-5 text-blue-600" />
                <h3 className="font-semibold text-gray-900">Login Required</h3>
              </div>
              <button onClick={() => setShowLoginPrompt(false)} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
            </div>
            <p className="text-gray-600 mb-4">Please login to {showLoginPrompt}.</p>
            <div className="flex gap-3">
              <Button onClick={() => navigate('/login')} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white">Login</Button>
              <Button onClick={() => setShowLoginPrompt(false)} variant="outline" className="flex-1">Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {/* Guest Banner */}
      {!user && showGuestBanner && (
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
          <div className="max-w-7xl mx-auto px-4 py-2">
            <div className="flex items-center justify-between">
              <span className="text-sm flex items-center gap-2"><Eye className="h-4 w-4" />Viewing as guest</span>
              <div className="flex items-center gap-2">
                <Button onClick={() => navigate('/login')} size="sm" className="bg-white text-blue-600 hover:bg-blue-50 text-xs px-3 py-1 h-7"><LogIn className="h-3 w-3 mr-1" />Login</Button>
                <button onClick={() => setShowGuestBanner(false)} className="text-white/80 hover:text-white"><X className="h-4 w-4" /></button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PWA Install Banner */}
      {showInstallBanner && !isInstalled && (
        <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white">
          <div className="max-w-7xl mx-auto px-4 py-2">
            <div className="flex items-center justify-between">
              <span className="text-sm flex items-center gap-2"><Smartphone className="h-4 w-4" />Install POLIVOX</span>
              <div className="flex items-center gap-2">
                <Button onClick={handleInstallClick} size="sm" className="bg-white text-green-600 hover:bg-green-50 text-xs px-3 py-1 h-7" data-testid="install-app-btn"><Download className="h-3 w-3 mr-1" />Install</Button>
                <button onClick={() => { setShowInstallBanner(false); localStorage.setItem('pwa_install_dismissed', Date.now().toString()); }} className="text-white/80 hover:text-white"><X className="h-4 w-4" /></button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* iOS Instructions Modal */}
      {showIOSInstructions && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl p-6 max-w-sm mx-4">
            <h3 className="font-semibold mb-3">Install POLIVOX</h3>
            <ol className="text-sm space-y-2 list-decimal list-inside text-gray-600">
              <li>Tap the Share button</li>
              <li>Scroll and tap &quot;Add to Home Screen&quot;</li>
              <li>Tap &quot;Add&quot;</li>
            </ol>
            <Button onClick={() => setShowIOSInstructions(false)} className="w-full mt-4 bg-green-600">Got it!</Button>
          </div>
        </div>
      )}

      {/* Quick Action Bar */}
      <div className="sticky top-0 z-40 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between py-2">
            <div className="flex items-center gap-2">
              <Button onClick={() => user ? navigate('/issues/new') : requireLogin('report issue')} size="sm" className={`rounded-full px-3 py-1.5 text-xs ${user ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-gray-100 text-gray-500'}`} data-testid="quick-report-issue">
                <FileText className="h-3.5 w-3.5 mr-1" />Issue
              </Button>
              <Button onClick={() => user ? navigate('/rooms/create') : requireLogin('start room')} size="sm" className={`rounded-full px-3 py-1.5 text-xs ${user ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white' : 'bg-gray-100 text-gray-500'}`}>
                <Swords className="h-3.5 w-3.5 mr-1" />Room
              </Button>
              {user ? (
                <Button onClick={() => {
                  if (!canParticipate()) {
                    triggerOnboarding();
                  } else {
                    setShowCreatePostModal(true);
                  }
                }} size="sm" variant="outline" className="rounded-full px-3 py-1.5 text-xs">
                  <PenSquare className="h-3.5 w-3.5 mr-1" />Post
                </Button>
              ) : (
                <Button onClick={() => navigate('/login')} size="sm" variant="outline" className="rounded-full px-3 py-1.5 text-xs border-blue-300 text-blue-600">
                  <LogIn className="h-3.5 w-3.5 mr-1" />Join
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 1. Hero Banner - Compact */}
      <section className="bg-gradient-to-b from-blue-50 to-white py-4 md:py-6">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 mb-1">See What&apos;s Happening</h1>
          <p className="text-sm text-gray-600 mb-3">Real issues. Real people. Real voices.</p>
          <div className="flex gap-2">
            <Button onClick={() => {
              if (user && !canParticipate()) {
                triggerOnboarding();
              } else {
                navigate('/issues/new');
              }
            }} className="bg-blue-600 text-white hover:bg-blue-700 text-sm px-4 py-2 rounded-full">
              Report Issue <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
            <Button onClick={() => navigate('/issues')} variant="outline" className="text-sm px-4 py-2 rounded-full">
              Explore
            </Button>
          </div>
        </div>
      </section>

      {/* 2. Leader Spotlight - Compact Rotating Carousel (2 Top + 2 Lowest Rated) */}
      {(topPerformers.top_leaders?.length > 0 || topPerformers.worst_leaders?.length > 0) && (
        <section className="py-2 bg-white border-b border-gray-100">
          <div className="max-w-2xl mx-auto px-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Trophy className="h-4 w-4 text-yellow-500" />
                <h2 className="text-sm font-semibold text-gray-900">Leader Spotlight</h2>
              </div>
              <button onClick={() => navigate('/leaders')} className="text-xs text-purple-600 hover:text-purple-700 flex items-center gap-1">
                View All <ArrowRight className="h-3 w-3" />
              </button>
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide" style={{ scrollbarWidth: 'none' }}>
              {/* Top Performers - Rotated based on offset */}
              {topPerformers.top_leaders?.length > 0 && (() => {
                const leaders = topPerformers.top_leaders;
                const start = spotlightOffset % leaders.length;
                const rotated = [...leaders.slice(start), ...leaders.slice(0, start)].slice(0, 2);
                return rotated.map((leader, idx) => (
                  <div 
                    key={`top-${leader.leader_id}-${spotlightOffset}-${idx}`}
                    onClick={() => navigate(`/leaders/${leader.leader_id}`)}
                    className="flex-shrink-0 w-40 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-2 cursor-pointer hover:shadow-md transition-shadow border border-green-100"
                  >
                    <div className="flex items-center gap-2 mb-1.5">
                      <img
                        src={leader.photo_url || '/logo.png'}
                        alt={leader.name}
                        className="w-10 h-10 rounded-full object-cover border-2 border-white shadow-sm"
                        onError={(e) => { e.target.src = '/logo.png'; }}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold text-gray-900 truncate">{leader.name}</p>
                        <p className="text-[10px] text-gray-500 truncate">{leader.role || 'MLA'} • {leader.party}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1">
                        <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                        <span className="text-xs font-medium text-gray-900">{(leader.avg_rating || 0).toFixed(1)}</span>
                      </div>
                      <span className="text-[9px] px-1.5 py-0.5 bg-green-100 text-green-700 rounded-full font-medium">Top</span>
                    </div>
                  </div>
                ));
              })()}
              {/* Lowest Rated - Rotated based on offset */}
              {topPerformers.worst_leaders?.length > 0 && (() => {
                const leaders = topPerformers.worst_leaders;
                const start = spotlightOffset % leaders.length;
                const rotated = [...leaders.slice(start), ...leaders.slice(0, start)].slice(0, 2);
                return rotated.map((leader, idx) => (
                  <div 
                    key={`worst-${leader.leader_id}-${spotlightOffset}-${idx}`}
                    onClick={() => navigate(`/leaders/${leader.leader_id}`)}
                    className="flex-shrink-0 w-40 bg-gradient-to-br from-orange-50 to-red-50 rounded-lg p-2 cursor-pointer hover:shadow-md transition-shadow border border-orange-100"
                  >
                    <div className="flex items-center gap-2 mb-1.5">
                      <img
                        src={leader.photo_url || '/logo.png'}
                        alt={leader.name}
                        className="w-10 h-10 rounded-full object-cover border-2 border-white shadow-sm"
                        onError={(e) => { e.target.src = '/logo.png'; }}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold text-gray-900 truncate">{leader.name}</p>
                        <p className="text-[10px] text-gray-500 truncate">{leader.role || 'MLA'} • {leader.party}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1">
                        <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                        <span className="text-xs font-medium text-gray-900">{(leader.avg_rating || 0).toFixed(1)}</span>
                      </div>
                      <span className="text-[9px] px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded-full font-medium">Improve</span>
                    </div>
                  </div>
                ));
              })()}
            </div>
          </div>
        </section>
      )}

      {/* Main Infinite Feed - Mixed with Widgets */}
      <section className="py-4 md:py-6 bg-gray-50">
        <div className="max-w-2xl mx-auto px-4">
          {/* Loading State */}
          {feedLoading && (
            <div className="space-y-4">
              <FeedItemSkeleton />
              <FeedItemSkeleton />
              <FeedItemSkeleton />
            </div>
          )}

          {/* Infinite Feed Items - Widgets naturally interspersed */}
          {!feedLoading && (
            <div className="space-y-4">
              {combinedFeed.map((item) => (
                <div key={item._feedKey}>
                  {renderFeedItem(item)}
                </div>
              ))}
            </div>
          )}

          {/* Loading More - Always shows spinner, never ends */}
          {loadingMore && (
            <div className="py-6 text-center">
              <Loader2 className="h-6 w-6 animate-spin mx-auto text-purple-600" />
            </div>
          )}

          {/* Empty State */}
          {!feedLoading && combinedFeed.length === 0 && (
            <div className="bg-white rounded-xl p-8 text-center">
              <FileText className="h-12 w-12 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500">No activity yet. Be the first!</p>
            </div>
          )}
        </div>
      </section>

      {/* 6. Information Button */}
      <div className="fixed bottom-20 right-4 z-30">
        <button
          onClick={() => setShowInfoPanel(!showInfoPanel)}
          className="bg-white shadow-lg rounded-full p-3 border border-gray-200 hover:bg-gray-50 transition-colors"
          data-testid="info-button"
        >
          <Info className="h-5 w-5 text-gray-600" />
        </button>
      </div>

      {/* Information Panel */}
      {showInfoPanel && (
        <div className="fixed bottom-32 right-4 z-30 bg-white rounded-xl shadow-xl border border-gray-200 w-56 overflow-hidden">
          <div className="p-3 border-b border-gray-100 flex items-center justify-between">
            <span className="text-sm font-medium text-gray-900">Information</span>
            <button onClick={() => setShowInfoPanel(false)} className="text-gray-400 hover:text-gray-600">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="p-2">
            {[
              { label: 'About', path: '/about' },
              { label: 'Contact', path: '/contact' },
              { label: 'Privacy Policy', path: '/privacy' },
              { label: 'Terms of Service', path: '/terms' },
              { label: 'Guidelines', path: '/guidelines' }
            ].map((item) => (
              <button
                key={item.path}
                onClick={() => { navigate(item.path); setShowInfoPanel(false); }}
                className="w-full text-left px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
              >
                {item.label}
              </button>
            ))}
          </div>
          <div className="p-3 border-t border-gray-100">
            <p className="text-xs text-gray-400 text-center">&copy; 2026 POLIVOX</p>
          </div>
        </div>
      )}

      {/* Create Post Modal */}
      {showCreatePostModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[85vh] flex flex-col overflow-hidden">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold">Create Post</h3>
              <button onClick={() => { setShowCreatePostModal(false); setNewPostContent(''); setNewPostImage(null); setNewPostImagePreview(null); }} className="p-1 hover:bg-gray-100 rounded"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              <div className="flex items-center gap-3 mb-4">
                <img src={user?.picture || '/logo.png'} alt="" className="w-10 h-10 rounded-full object-cover" />
                <div>
                  <p className="font-medium text-gray-900">{user?.name}</p>
                  <p className="text-xs text-gray-500">Posting to feed</p>
                </div>
              </div>
              <textarea value={newPostContent} onChange={(e) => setNewPostContent(e.target.value)} placeholder="What's on your mind?" className="w-full h-32 p-3 border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-purple-100" maxLength={500} />
              <div className="flex justify-between text-xs mt-1">
                <span className={newPostContent.trim().length < 10 ? "text-red-500" : "text-green-500"}>{newPostContent.trim().length < 10 ? `Min 10 chars (${newPostContent.trim().length}/10)` : 'Ready'}</span>
                <span className="text-gray-400">{newPostContent.length}/500</span>
              </div>
              {newPostImagePreview && (
                <div className="relative mt-3">
                  <img src={newPostImagePreview} alt="Preview" className="w-full max-h-[200px] object-contain rounded-lg bg-gray-100" />
                  <button onClick={() => { setNewPostImage(null); setNewPostImagePreview(null); }} className="absolute top-2 right-2 p-1.5 bg-gray-800/70 rounded-full text-white"><X className="h-4 w-4" /></button>
                </div>
              )}
            </div>
            <div className="p-4 border-t flex items-center justify-between">
              <label className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 cursor-pointer">
                <ImageIcon className="h-5 w-5 text-green-600" />
                <span className="text-sm text-gray-600">Photo</span>
                <input type="file" accept="image/*" onChange={handlePostImageChange} className="hidden" />
              </label>
              <Button onClick={handleCreatePost} disabled={postingInProgress || newPostContent.trim().length < 10} className="px-6">
                {postingInProgress ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Posting...</> : 'Post'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Image Expand Modal */}
      {expandedImage && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4" onClick={() => setExpandedImage(null)}>
          <button onClick={() => setExpandedImage(null)} className="absolute top-4 right-4 p-2 bg-white/10 rounded-full text-white hover:bg-white/20"><X className="h-6 w-6" /></button>
          <img src={expandedImage} alt="Expanded" className="max-w-full max-h-full object-contain" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
};

export default Home;
